import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Compass, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { TOUR } from "@/lib/tourStops";
import { api } from "@/lib/api";

const save = (body) => api.put("/help/tour", body).catch(() => {});

/** A first-run walkthrough: it drives the router, one stop per module. */
export const GuidedTour = () => {
  const navigate = useNavigate();
  const [stop, setStop] = useState(-1);
  const [stops, setStops] = useState(TOUR);
  const startedRef = useRef(false);

  const begin = useCallback((from = 0, list = TOUR) => {
    startedRef.current = true;
    setStop(from);
    navigate(list[from].route);
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    api.get("/help/tour").then(({ data }) => {
      const scope = data.scope || [];
      const mine = TOUR.filter((s) => !s.res || scope.includes(s.res));
      if (cancelled) return;
      setStops(mine.length ? mine : TOUR);
      if (data.seen || startedRef.current) return;
      begin(Math.min(Math.max(data.last_stop || 0, 0), (mine.length || TOUR.length) - 1),
            mine.length ? mine : TOUR);
    }).catch(() => {});
    const start = () => begin(0, stops);
    window.addEventListener("servecor:start-tour", start);
    return () => { cancelled = true; window.removeEventListener("servecor:start-tour", start); };
  }, [begin, stops]);

  useEffect(() => {
    if (stop < 0) return undefined;
    const node = document.querySelector(`[data-testid="${stops[stop].nav}"]`);
    if (node) {
      node.style.outline = "2px solid hsl(var(--primary))";
      node.style.outlineOffset = "1px";
      node.style.borderRadius = "10px";
    }
    return () => {
      if (node) { node.style.outline = ""; node.style.outlineOffset = ""; }
    };
  }, [stop, stops]);

  if (stop < 0) return null;
  const current = stops[stop];
  const last = stop === stops.length - 1;

  const go = (next) => {
    setStop(next);
    navigate(stops[next].route);
    save({ last_stop: next });
  };

  const close = (completed) => {
    setStop(-1);
    save({ completed, dismissed: !completed, last_stop: stop });
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70] w-[min(400px,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border border-primary/40 bg-card shadow-[0_24px_60px_-24px_rgba(0,0,0,0.6)]"
         data-testid="guided-tour">
      <div className="flex items-center gap-2 border-b border-border bg-primary/10 px-4 py-2.5">
        <Compass className="h-3.5 w-3.5 text-primary" />
        <span className="flex-1 font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
          Guided tour · {stop + 1} of {stops.length}
        </span>
        <button type="button" onClick={() => close(false)} data-testid="tour-close"
                className="text-muted-foreground transition-colors hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-2 px-4 py-3.5">
        <div className="text-[13.5px] font-semibold text-foreground" data-testid="tour-title">
          {current.title}
        </div>
        <p className="text-[12px] leading-relaxed text-muted-foreground" data-testid="tour-text">
          {current.text}
        </p>
        <button type="button" data-testid="tour-read-chapter"
                onClick={() => navigate(`/help?chapter=${current.chapter}`)}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary hover:underline">
          <BookOpen className="h-3 w-3" /> Read the manual chapter
        </button>
      </div>
      <div className="h-1 w-full bg-muted">
        <div className="h-1 bg-primary transition-all duration-300"
             style={{ width: `${((stop + 1) / stops.length) * 100}%` }} />
      </div>
      <div className="flex items-center gap-2 px-4 py-3">
        <Button size="sm" variant="ghost" onClick={() => go(Math.max(0, stop - 1))}
                disabled={stop === 0} data-testid="tour-back"
                className="h-8 rounded-full px-2.5 text-[11px] text-muted-foreground">
          <ArrowLeft className="mr-1 h-3 w-3" /> Back
        </Button>
        <Button size="sm" variant="ghost" onClick={() => close(false)} data-testid="tour-skip"
                className="h-8 rounded-full px-2.5 text-[11px] text-muted-foreground">
          Skip for now
        </Button>
        <div className="flex-1" />
        {last ? (
          <Button size="sm" onClick={() => close(true)} data-testid="tour-finish"
                  className="h-8 rounded-full bg-primary px-3.5 text-[11px] text-primary-foreground hover:bg-primary/90">
            Finish · don't show again
          </Button>
        ) : (
          <Button size="sm" onClick={() => go(stop + 1)} data-testid="tour-next"
                  className="h-8 rounded-full bg-primary px-3.5 text-[11px] text-primary-foreground hover:bg-primary/90">
            Next <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
};
